import { useCallback, useMemo } from "react";
import { Marker } from "mapbox-gl";

import { Coordinate } from "common/types";
import CommonMap from "common/components/Map";

import { MapProps } from "client/common/types";

import Loading from "../loading/Loading";
import ErrorContainer from "../errors/ErrorContainer";

import styles from "../../styles/Map.module.less";
import styleVars from "../../styles/export-variables.less?export";

const MAPBOX_CLIENT_STYLE = "mapbox://styles/vetcove/cldnb0hv4000j01pdhmk5tqoo";
const MAP_PATH_OPTIONS = {
  paint: {
    "line-color": styleVars.mapMarkerColor,
    "line-width": 5,
  },
};

const renderError = () => (
  <ErrorContainer
    size="auto-fill"
    title="Map unavailable"
    description="We couldn't load the map."
    imageAlt="Map unavailable"
  />
);

/** Renders a Map using Mapbox-GL JS
 * When using this componenet, lazy-load it to reduce bundle size (mapbox-gl is 30+mb)
 */
const Map = ({ markerStyle = "default", ...commonMapProps }: MapProps) => {
  const createMarker = useCallback(
    ([lng, lat]: Coordinate) => {
      if (markerStyle === "paw") {
        const customMarker = document.createElement("div");
        customMarker.className = styles.marker;

        return new Marker({ element: customMarker }).setLngLat([lng, lat]);
      }
      return new Marker({
        color: styleVars.mapMarkerColor,
        scale: 0.75,
      }).setLngLat([lng, lat]);
    },
    [markerStyle],
  );
  const renderLoading = useCallback(() => <Loading />, []);
  const pathOptions = useMemo(() => MAP_PATH_OPTIONS, []);

  return (
    <CommonMap
      {...commonMapProps}
      mapStyle={MAPBOX_CLIENT_STYLE}
      createMarker={createMarker}
      pathOptions={pathOptions}
      renderLoading={renderLoading}
      renderError={renderError}
    />
  );
};

export default Map;
